"use client";

import { useMemo, useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { X, Plus, Trash2, Upload, FileCheck, ArrowRight } from "lucide-react";
import {
  onboardingRequirementsAction,
  submitOnboardingAction,
  uploadDocumentAction,
} from "@/app/actions";
import type { RequirementsResponse, ReqFieldDef } from "@/lib/conduit";
import { CountrySelect, CountryFlag, countryName } from "@/components/country";
import { iso2to3, iso3to2 } from "@/lib/iso3";

type Vals = Record<string, unknown>;
type Doc = { id: string; fileName: string };
type PersonState = { values: Vals; docs: Doc[] };

/** Conduit's onboarding requirements group fields in this order; we present them as steps. */
const GROUP_STEPS: Array<{ title: string; groups: string[] }> = [
  { title: "Business", groups: ["businessInfo"] },
  { title: "Addresses", groups: ["registeredAddress", "operatingAddress"] },
  { title: "Classification", groups: ["companyClassification"] },
  { title: "Activity", groups: ["businessActivity"] },
  { title: "Compliance", groups: ["regulatoryHistory"] },
];

function isVisible(f: ReqFieldDef, vals: Vals): boolean {
  for (const c of f.conditions ?? []) {
    if (!c.pointer) continue;
    const v = vals[c.pointer];
    if (c.operator === "is_false" && v !== false) return false;
    if (c.operator === "is_true" && v !== true) return false;
  }
  return true;
}

function isEmpty(v: unknown): boolean {
  return v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
}

/** Required + visible + unanswered. Certification booleans additionally must be true. */
function missingFields(fields: ReqFieldDef[], vals: Vals): ReqFieldDef[] {
  return fields.filter((f) => {
    if (!f.required || !f.pointer || !isVisible(f, vals)) return false;
    const v = vals[f.pointer];
    if (f.type === "boolean") {
      return f.pointer.startsWith("/certification") ? v !== true : typeof v !== "boolean";
    }
    return isEmpty(v);
  });
}

function setDeep(obj: Record<string, unknown>, path: string[], value: unknown) {
  let cur = obj;
  for (let i = 0; i < path.length - 1; i++) {
    cur[path[i]] = cur[path[i]] ?? {};
    cur = cur[path[i]] as Record<string, unknown>;
  }
  cur[path[path.length - 1]] = value;
}

function coerce(f: ReqFieldDef, v: unknown): unknown {
  if ((f.type === "number" || f.type === "integer") && typeof v === "string" && v !== "") return Number(v);
  return v;
}

/** Nested payload subtree from answered, visible fields ("/a/b" pointers → {a:{b}}). */
function buildFromFields(fields: ReqFieldDef[], vals: Vals): Record<string, unknown> {
  const root: Record<string, unknown> = {};
  for (const f of fields) {
    if (!f.pointer || !isVisible(f, vals)) continue;
    const v = vals[f.pointer];
    if (isEmpty(v)) continue;
    setDeep(root, f.pointer.split("/").filter(Boolean), coerce(f, v));
  }
  return root;
}

function Iso3CountrySelect({ value, onChange }: { value: string; onChange: (iso3: string) => void }) {
  return <CountrySelect value={value ? iso3to2(value) : ""} onChange={(c) => onChange(iso2to3(c))} />;
}

/** Multi-country picker (ISO3 chips) for enumArrays like countriesOfActivity. */
function Iso3CountryMultiSelect({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  return (
    <div>
      <CountrySelect
        value=""
        placeholder="Add a country…"
        onChange={(c) => {
          const v3 = iso2to3(c);
          if (!value.includes(v3)) onChange([...value, v3]);
        }}
      />
      {value.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {value.map((v3) => (
            <button
              key={v3}
              type="button"
              className="pill"
              style={{ background: "var(--c-charcoal)", color: "var(--c-white)" }}
              title="Remove"
              onClick={() => onChange(value.filter((x) => x !== v3))}
            >
              <CountryFlag code={iso3to2(v3)} size={12} /> {countryName(iso3to2(v3))} ×
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function FieldControl({
  f,
  value,
  onChange,
}: {
  f: ReqFieldDef;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const cons = (f.constraints ?? {}) as { pattern?: string; minDate?: string; maxDate?: string; min?: number; max?: number; example?: string };
  const opts =
    f.options ??
    (f.allowedValues ?? []).map((v) => ({ value: String(v), label: String(v), description: undefined as string | undefined }));

  if (f.type === "boolean") {
    return (
      <div className="flex gap-2">
        {[true, false].map((b) => (
          <button
            key={String(b)}
            type="button"
            className="pill"
            style={
              value === b
                ? { background: "var(--c-charcoal)", color: "var(--c-white)" }
                : { boxShadow: "inset 0 0 0 1px var(--c-hairline)", color: "var(--c-ink-soft)" }
            }
            onClick={() => onChange(b)}
          >
            {b ? "yes" : "no"}
          </button>
        ))}
      </div>
    );
  }

  if (f.type === "country") {
    return <Iso3CountrySelect value={typeof value === "string" ? value : ""} onChange={onChange} />;
  }

  if (f.type === "enumArray") {
    const arr = Array.isArray(value) ? (value as string[]) : [];
    // Country-sized lists get a searchable picker; short lists get toggle pills.
    if (opts.length > 30) {
      return <Iso3CountryMultiSelect value={arr} onChange={onChange} />;
    }
    return (
      <div className="flex flex-wrap gap-2">
        {opts.map((o) => {
          const on = arr.includes(o.value);
          return (
            <button
              key={o.value}
              type="button"
              className="pill"
              title={o.description}
              style={
                on
                  ? { background: "var(--c-charcoal)", color: "var(--c-white)" }
                  : { boxShadow: "inset 0 0 0 1px var(--c-hairline)", color: "var(--c-ink-soft)" }
              }
              onClick={() => onChange(on ? arr.filter((x) => x !== o.value) : [...arr, o.value])}
            >
              {o.label ?? o.value}
            </button>
          );
        })}
      </div>
    );
  }

  if (f.type === "enum") {
    return (
      <select className="field" value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value)}>
        <option value="">Select…</option>
        {opts.map((o) => (
          <option key={o.value} value={o.value} title={o.description}>
            {o.label ?? o.value}
          </option>
        ))}
      </select>
    );
  }

  if (f.type === "date") {
    const today = new Date().toISOString().slice(0, 10);
    return (
      <input
        className="field"
        type="date"
        min={cons.minDate}
        max={cons.maxDate === "today" ? today : cons.maxDate}
        value={typeof value === "string" ? value : ""}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  const inputType =
    f.type === "email" ? "email" : f.type === "phone" ? "tel" : f.type === "url" ? "url" : f.type === "number" || f.type === "integer" ? "number" : "text";
  return (
    <input
      className="field"
      type={inputType}
      inputMode={f.type === "number" || f.type === "integer" ? "decimal" : undefined}
      min={cons.min}
      max={cons.max}
      placeholder={cons.example ?? (f.type === "url" ? "https://" : "")}
      value={typeof value === "string" || typeof value === "number" ? String(value) : ""}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function Field({
  f,
  value,
  onChange,
}: {
  f: ReqFieldDef;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const wide = f.type === "enumArray" || (f.pointer ?? "").toLowerCase().includes("description");
  return (
    <div className={wide ? "col-span-full" : ""}>
      <label className="label">
        {f.label ?? f.pointer}
        {f.required ? "" : " (optional)"}
      </label>
      <FieldControl f={f} value={value} onChange={onChange} />
      {f.helpText && (
        <p className="mt-1 text-[11px] font-light" style={{ color: "var(--c-ink-soft)" }}>
          {f.helpText}
        </p>
      )}
    </div>
  );
}

export default function OnboardingModal({ onClose }: { onClose: () => void }) {
  const router = useRouter();
  const [pending, start] = useTransition();
  const [country, setCountry] = useState(""); // ISO2 for the picker
  const [reqs, setReqs] = useState<RequirementsResponse | null>(null);
  const [loadingReqs, setLoadingReqs] = useState(false);
  const [step, setStep] = useState(0); // 0 = country; then GROUP_STEPS; then People, Documents, Review
  const [values, setValues] = useState<Vals>({});
  const [persons, setPersons] = useState<PersonState[]>([{ values: {}, docs: [] }]);
  const [orgDocs, setOrgDocs] = useState<Doc[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Array<{ pointer?: string; detail?: string }>>([]);
  const [done, setDone] = useState<{ id: string; status: string } | null>(null);

  const groupSteps = useMemo(
    () => GROUP_STEPS.filter((s) => s.groups.some((g) => (reqs?.fields ?? []).some((f) => f.group === g))),
    [reqs],
  );
  // Step layout: [Country] + groupSteps + [People, Documents, Review]
  const stepTitles = ["Country", ...groupSteps.map((s) => s.title), "People", "Documents", "Review"];
  const peopleStep = 1 + groupSteps.length;
  const docsStep = peopleStep + 1;
  const reviewStep = docsStep + 1;

  const personDef = useMemo(
    () => reqs?.individualRequirements?.find((r) => r.role === "any") ?? reqs?.individualRequirements?.[0],
    [reqs],
  );
  const personFields = personDef?.fields ?? [];
  const roleMinimums = (reqs?.individualRequirements ?? []).filter((r) => r.role && r.role !== "any" && (r.minCount ?? 0) > 0);

  const certFields = (reqs?.fields ?? []).filter((f) => f.group === "certification");

  function fieldsForStep(i: number): ReqFieldDef[] {
    const gs = groupSteps[i - 1];
    if (!gs) return [];
    return (reqs?.fields ?? []).filter((f) => gs.groups.includes(f.group ?? ""));
  }

  function loadRequirements() {
    setLoadingReqs(true);
    setError(null);
    onboardingRequirementsAction(country).then((res) => {
      setLoadingReqs(false);
      if (!res.ok) {
        setError(res.error);
        return;
      }
      setReqs(res.data);
      const iso3 = iso2to3(country);
      // Sensible defaults: addresses start in the chosen country, operating = registered.
      setValues((v) => ({
        "/registeredAddress/country": iso3,
        "/operatingAddress/country": iso3,
        "/operatingAddress/sameAsRegistered": true,
        ...v,
      }));
      setStep(1);
    });
  }

  async function upload(files: FileList | null, purpose: string, add: (d: Doc) => void) {
    if (!files) return;
    setError(null);
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.set("file", file);
      fd.set("purpose", purpose);
      fd.set("name", file.name);
      const res = await uploadDocumentAction(fd);
      if (res.ok) add({ id: res.data.id, fileName: res.data.fileName ?? file.name });
      else setError(`${file.name}: ${res.error}`);
    }
  }

  function personRoles(p: PersonState): string[] {
    return Array.isArray(p.values["/roles"]) ? (p.values["/roles"] as string[]) : [];
  }

  /** Persons with owner/controller roles must carry an identity document (Conduit 422s otherwise). */
  function personNeedsDoc(p: PersonState): boolean {
    const roles = personRoles(p);
    return roles.includes("BENEFICIAL_OWNER") || roles.includes("CONTROLLING_PERSON");
  }

  const unmetRoleMinimums = roleMinimums.filter(
    (r) => persons.filter((p) => personRoles(p).includes(r.role!)).length < (r.minCount ?? 0),
  );
  const personProblems =
    persons.flatMap((p, i) => missingFields(personFields, p.values).map(() => i)).length +
    persons.filter((p) => personNeedsDoc(p) && p.docs.length === 0).length +
    unmetRoleMinimums.length;

  const missingByStep = (i: number) => missingFields(fieldsForStep(i), values).length;
  const docsMissing = orgDocs.length < (reqs?.minDocuments ?? 1);
  const certMissing = missingFields(certFields, values).length;

  const totalBlockers =
    groupSteps.reduce((n, _, i) => n + missingByStep(i + 1), 0) + personProblems + (docsMissing ? 1 : 0) + certMissing;

  function buildPayload() {
    const topFields = (reqs?.fields ?? []).filter((f) => f.group !== "certification");
    const root = buildFromFields(topFields, values);
    const legalName = (values["/businessInfo/legalName"] as string) ?? "";
    return {
      ...root,
      clientReferenceId: legalName.toLowerCase().replaceAll(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "onboarding",
      documentIds: orgDocs.map((d) => d.id),
      ownership: {
        persons: persons.map((p) => ({
          ...buildFromFields(personFields, p.values),
          ...(p.docs.length ? { documentIds: p.docs.map((d) => d.id) } : {}),
        })),
      },
      certification: buildFromFields(certFields, values).certification ?? {},
    };
  }

  function submit() {
    setError(null);
    setFieldErrors([]);
    start(async () => {
      const res = await submitOnboardingAction(buildPayload());
      if (res.ok) {
        setDone(res.data);
        router.refresh();
      } else {
        setError(res.error);
        setFieldErrors(res.fieldErrors ?? []);
      }
    });
  }

  const grid = "grid grid-cols-1 gap-4 sm:grid-cols-2";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(35,35,35,0.55)" }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="card flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden" style={{ background: "var(--c-white)" }}>
        {/* Header */}
        <div className="flex items-center justify-between border-b px-8 py-5" style={{ borderColor: "var(--c-hairline)" }}>
          <div>
            <div className="eyebrow" style={{ color: "var(--c-ink-soft)" }}>
              new customer
            </div>
            <h2 className="display text-[24px]">Onboard a business</h2>
          </div>
          <button type="button" className="btn btn-ghost !px-3 !py-2" onClick={onClose} title="Close">
            <X size={16} />
          </button>
        </div>

        {/* Step pills */}
        {!done && reqs && (
          <div className="flex flex-wrap gap-1.5 border-b px-8 py-3" style={{ borderColor: "var(--c-hairline)" }}>
            {stepTitles.map((t, i) => (
              <button
                key={t}
                type="button"
                className="pill"
                style={
                  i === step
                    ? { background: "var(--c-green)", color: "var(--c-charcoal)" }
                    : i < step
                      ? { background: "var(--c-charcoal)", color: "var(--c-white)" }
                      : { boxShadow: "inset 0 0 0 1px var(--c-hairline)", color: "var(--c-ink-soft)" }
                }
                onClick={() => i > 0 && setStep(i)}
              >
                {t.toLowerCase()}
              </button>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          {done ? (
            <div className="card-green p-8">
              <div className="display text-[24px]">Application submitted</div>
              <p className="mt-2 text-[14px] font-light">
                <span className="mono">{done.id}</span> is <strong>{done.status.replaceAll("_", " ")}</strong>. Conduit's
                compliance team reviews it from here — track progress on the{" "}
                <Link href={`/onboarding/${done.id}`} className="underline" onClick={onClose}>
                  Onboarding page
                </Link>
                .
              </p>
            </div>
          ) : step === 0 ? (
            <div className="max-w-md">
              <label className="label">Where is the business incorporated?</label>
              <CountrySelect value={country} onChange={setCountry} />
              <p className="mt-2 text-[12px] font-light" style={{ color: "var(--c-ink-soft)" }}>
                Conduit's requirements — fields, people, and documents — depend on the country of incorporation. We'll
                fetch the exact checklist for this country next.
              </p>
              <button className="btn btn-primary mt-6" disabled={!country || loadingReqs} onClick={loadRequirements}>
                {loadingReqs ? "Loading requirements…" : "Start"} <ArrowRight size={14} />
              </button>
            </div>
          ) : step >= 1 && step <= groupSteps.length ? (
            <div className={grid}>
              {fieldsForStep(step)
                .filter((f) => isVisible(f, values))
                .map((f) => (
                  <Field
                    key={f.pointer}
                    f={f}
                    value={values[f.pointer!]}
                    onChange={(v) => setValues((x) => ({ ...x, [f.pointer!]: v }))}
                  />
                ))}
            </div>
          ) : step === peopleStep ? (
            <div className="flex flex-col gap-4">
              <p className="text-[13px] font-light" style={{ color: "var(--c-ink-soft)" }}>
                Add every beneficial owner (≥25% ownership), at least one controlling person (CEO/CFO or equivalent),
                and at least one legal representative. One person can hold several roles.
              </p>
              {unmetRoleMinimums.length > 0 && (
                <div className="card-cream p-3 text-[13px] font-light">
                  Still needed:{" "}
                  {unmetRoleMinimums.map((r) => `${r.minCount} × ${r.role!.replaceAll("_", " ").toLowerCase()}`).join(", ")}
                </div>
              )}
              {persons.map((p, i) => (
                <div key={i} className="card-cream p-5">
                  <div className="mb-4 flex items-center justify-between">
                    <div className="font-medium">Person {i + 1}</div>
                    {persons.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-ghost !px-3 !py-1.5 text-[13px]"
                        onClick={() => setPersons((ps) => ps.filter((_, j) => j !== i))}
                      >
                        <Trash2 size={13} /> Remove
                      </button>
                    )}
                  </div>
                  <div className={grid}>
                    {personFields
                      .filter((f) => isVisible(f, p.values))
                      .map((f) => (
                        <Field
                          key={f.pointer}
                          f={f}
                          value={p.values[f.pointer!]}
                          onChange={(v) =>
                            setPersons((ps) => ps.map((q, j) => (j === i ? { ...q, values: { ...q.values, [f.pointer!]: v } } : q)))
                          }
                        />
                      ))}
                  </div>
                  <div className="mt-4">
                    <label className="label">
                      Identity document{personNeedsDoc(p) ? "" : " (optional)"}
                    </label>
                    <p className="mb-2 text-[11px] font-light" style={{ color: "var(--c-ink-soft)" }}>
                      Passport or national ID. Required for beneficial owners and controlling persons.
                    </p>
                    <label
                      className="flex cursor-pointer items-center gap-3 rounded-[12px] border border-dashed p-3"
                      style={{ borderColor: "var(--c-hairline)", background: "var(--c-white)" }}
                    >
                      <Upload size={14} />
                      <span className="text-[13px] font-light">Upload PDF, JPG or PNG</span>
                      <input
                        type="file"
                        hidden
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) =>
                          upload(e.target.files, "kyc", (d) =>
                            setPersons((ps) => ps.map((q, j) => (j === i ? { ...q, docs: [...q.docs, d] } : q))),
                          )
                        }
                      />
                    </label>
                    {p.docs.map((d) => (
                      <div key={d.id} className="mt-2 flex items-center gap-2 text-[13px] font-light">
                        <FileCheck size={13} /> {d.fileName}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-ghost self-start"
                onClick={() => setPersons((ps) => [...ps, { values: {}, docs: [] }])}
              >
                <Plus size={14} /> Add person
              </button>
            </div>
          ) : step === docsStep ? (
            <div>
              {reqs?.documents && reqs.documents.length > 0 && (
                <div className="card-cream mb-5 p-4 text-[14px] font-light">
                  <div className="eyebrow mb-2" style={{ color: "var(--c-ink-soft)" }}>
                    expected for {reqs.country}
                  </div>
                  <ul className="list-inside list-disc">
                    {reqs.documents.map((d, i) => (
                      <li key={i}>
                        {(d.alternatives ?? []).length > 0
                          ? (d.alternatives ?? []).map((a) => a.title).join(" or ")
                          : d.title ?? d.canonicalType}
                        {d.from ? ` — ${d.from}` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <label
                className="flex cursor-pointer flex-col items-center gap-2 rounded-[16px] border border-dashed p-10 text-center"
                style={{ borderColor: "var(--c-hairline)" }}
              >
                <Upload size={22} />
                <span className="text-[15px] font-light">Drop files or click to upload (PDF, JPG, PNG — max 10 MB)</span>
                <input
                  type="file"
                  multiple
                  hidden
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => upload(e.target.files, "organization_onboarding", (d) => setOrgDocs((x) => [...x, d]))}
                />
              </label>
              {orgDocs.map((d) => (
                <div key={d.id} className="mt-3 flex items-center gap-2 text-[14px] font-light">
                  <FileCheck size={14} /> {d.fileName}
                  <button
                    type="button"
                    className="ml-auto text-[12px] underline"
                    onClick={() => setOrgDocs((x) => x.filter((y) => y.id !== d.id))}
                  >
                    remove
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div>
              <div className="mb-6 grid grid-cols-2 gap-x-8 gap-y-3 text-[14px] font-light">
                <div>
                  <span className="label">Business</span>
                  {(values["/businessInfo/legalName"] as string) || "—"}
                </div>
                <div>
                  <span className="label">Country</span>
                  {countryName(country)}
                </div>
                <div>
                  <span className="label">People</span>
                  {persons
                    .map((p) => `${p.values["/firstName"] ?? ""} ${p.values["/lastName"] ?? ""}`.trim() || "—")
                    .join(" · ")}
                </div>
                <div>
                  <span className="label">Documents</span>
                  {orgDocs.length} company · {persons.reduce((n, p) => n + p.docs.length, 0)} identity
                </div>
              </div>

              {totalBlockers > 0 && (
                <div className="card-cream mb-5 p-4 text-[13px] font-light">
                  {totalBlockers} item{totalBlockers === 1 ? "" : "s"} still missing — use the step pills above to jump
                  back and complete them.
                </div>
              )}

              <div className="flex flex-col gap-3">
                {certFields.map((f) => (
                  <label key={f.pointer} className="flex items-start gap-3 text-[13px] font-light">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={values[f.pointer!] === true}
                      onChange={(e) => setValues((x) => ({ ...x, [f.pointer!]: e.target.checked }))}
                    />
                    <span>
                      {f.label}
                      {f.required ? "" : " (optional)"}
                    </span>
                  </label>
                ))}
              </div>

              <button className="btn btn-primary mt-6" disabled={pending || totalBlockers > 0} onClick={submit}>
                {pending ? "Submitting…" : "Submit to Conduit"}
              </button>
            </div>
          )}

          {error && !done && (
            <div className="card-cream mt-5 p-4 text-[14px] font-light" style={{ color: "var(--c-danger)" }}>
              {error}
              {fieldErrors.length > 0 && (
                <ul className="mt-2 list-inside list-disc" style={{ color: "var(--c-charcoal)" }}>
                  {fieldErrors.map((f, i) => (
                    <li key={i}>
                      <span className="mono text-[12px]">{f.pointer}</span> {f.detail}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Footer nav */}
        {!done && reqs && step > 0 && (
          <div className="flex items-center justify-between border-t px-8 py-4" style={{ borderColor: "var(--c-hairline)" }}>
            <button className="btn btn-ghost" onClick={() => setStep((s) => s - 1)}>
              Back
            </button>
            {step >= 1 && step <= groupSteps.length && missingByStep(step) > 0 && (
              <span className="text-[12px] font-light" style={{ color: "var(--c-ink-soft)" }}>
                {missingByStep(step)} required field{missingByStep(step) === 1 ? "" : "s"} remaining
              </span>
            )}
            {step < reviewStep && (
              <button className="btn btn-dark" onClick={() => setStep((s) => s + 1)}>
                Continue
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
